import React from "react";
import { Accordion, Alert, Badge, Table } from "react-bootstrap";
import { useAuthenticatedFetch } from "../lib/api";
import { rebalanceCheckPath } from "./lib";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";

type CheckData = UnpackResponse<RebalancesController["getRebalanceCheck"]>;

interface Finding {
  code: string;
  severity: "critical" | "error" | "warning" | "info";
  title: string;
  description: string;
  recommendation?: string;
  agent?: string;
  account_id?: number;
  symbol?: string;
}

// APP-1282: exported so the admin list reuses the SAME status→variant mapping
// for its check column (action_required→danger, review_recommended→warning,
// pass→success, unchecked/unknown→secondary).
export const statusVariant = (
  status: string,
): "success" | "warning" | "danger" | "secondary" => {
  switch (status) {
    case "pass":
      return "success";
    case "review_recommended":
      return "warning";
    case "action_required":
      return "danger";
    default:
      return "secondary";
  }
};

// APP-1282: exported for reuse by the admin list's severity badges.
export const severityVariant = (
  severity: string,
): "danger" | "warning" | "info" | "secondary" => {
  switch (severity) {
    case "critical":
    case "error":
      return "danger";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "secondary";
  }
};

// APP-1282: exported so the admin list shows the same human-readable status.
export const statusLabel = (status: string): string => {
  switch (status) {
    case "pass":
      return "Pass";
    case "review_recommended":
      return "Review Recommended";
    case "action_required":
      return "Action Required";
    default:
      return status;
  }
};

const RebalanceCheckPanel: React.FC<{ rebalanceId: number }> = ({
  rebalanceId,
}) => {
  const { data, isPending } = useAuthenticatedFetch<CheckData>(
    rebalanceCheckPath(rebalanceId),
    {},
    { enabled: !!rebalanceId },
  );

  if (isPending) return null;

  const check = data?.data;
  if (!check) {
    return <p className="text-muted mb-3">No check results available.</p>;
  }

  const findings: Finding[] = Array.isArray(check.findings)
    ? (check.findings as unknown as Finding[])
    : [];
  const recommendations: string[] = Array.isArray(check.recommendations)
    ? check.recommendations
    : [];

  const groupedFindings: Record<string, Finding[]> = {};
  for (const f of findings) {
    const sev = f.severity || "info";
    if (!groupedFindings[sev]) groupedFindings[sev] = [];
    groupedFindings[sev].push(f);
  }

  const severityOrder = ["critical", "error", "warning", "info"];

  return (
    <div className="mb-4">
      <Alert variant="light" className="border">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <Badge
              bg={statusVariant(check.overallStatus)}
              className="fs-6 px-2 py-1"
            >
              {check.overallScore}/100
            </Badge>
            <Badge bg={statusVariant(check.overallStatus)}>
              {statusLabel(check.overallStatus)}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            {check.findingCounts?.critical > 0 && (
              <Badge bg="danger">{check.findingCounts.critical} critical</Badge>
            )}
            {check.findingCounts?.error > 0 && (
              <Badge bg="danger">
                {check.findingCounts.error} error
                {check.findingCounts.error !== 1 && "s"}
              </Badge>
            )}
            {check.findingCounts?.warning > 0 && (
              <Badge bg="warning">
                {check.findingCounts.warning} warning
                {check.findingCounts.warning !== 1 && "s"}
              </Badge>
            )}
            {check.findingCounts?.info > 0 && (
              <Badge bg="info">{check.findingCounts.info} info</Badge>
            )}
          </div>
        </div>

        {check.summary && <p className="mb-2">{check.summary}</p>}

        {findings.length > 0 && (
          <Accordion className="mb-2">
            {severityOrder
              .filter((sev) => groupedFindings[sev]?.length)
              .map((sev) => (
                <Accordion.Item eventKey={sev} key={sev}>
                  <Accordion.Header>
                    <Badge bg={severityVariant(sev)} className="me-2">
                      {sev.toUpperCase()}
                    </Badge>
                    {groupedFindings[sev].length} finding
                    {groupedFindings[sev].length !== 1 && "s"}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table size="sm" bordered>
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Recommendation</th>
                          <th>Context</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedFindings[sev].map((f, idx) => (
                          <tr
                            key={`${f.code}-${idx}`}
                            className={
                              sev === "critical" ? "fw-bold" : undefined
                            }
                          >
                            <td>
                              <code>{f.code}</code>
                            </td>
                            <td>{f.title}</td>
                            <td>{f.description}</td>
                            <td>{f.recommendation || "-"}</td>
                            <td>
                              {f.agent && (
                                <small className="d-block">{f.agent}</small>
                              )}
                              {f.account_id && (
                                <small className="d-block">
                                  Acct {f.account_id}
                                </small>
                              )}
                              {f.symbol && (
                                <small className="d-block">{f.symbol}</small>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
          </Accordion>
        )}

        {recommendations.length > 0 && (
          <div>
            <strong>Recommendations</strong>
            <ul className="mb-0">
              {recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </Alert>
    </div>
  );
};

export default RebalanceCheckPanel;
